import { useLocalStorage } from "@elit-hair/elit-tools";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import type { SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { showNotificationToast } from "@/components/notifications/notification-toast";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import {
    AppointmentNotification,
    AppointmentStatusUpdateNotification,
    AppointmentStatusUpdatePayload,
} from "@/types/call.types";

import {
    StoredAppointmentNotification,
    deserializeAppointments,
    serializeAppointments,
    toStoredAppointment,
} from "./persisted-notifications";

/**
 * Hook for handling appointment notifications
 * Manages appointment notification logic including toasts and state management
 */
export const useAppointmentNotification = () => {
    const { t } = useTranslation();
    const [storedNotifications, setStoredNotifications] = useLocalStorage<StoredAppointmentNotification[]>(
        LOCAL_STORAGE_KEYS.PENDING_APPOINTMENT_NOTIFICATIONS.key,
        LOCAL_STORAGE_KEYS.PENDING_APPOINTMENT_NOTIFICATIONS.default
    );

    const pendingAppointmentNotifications = useMemo(
        () => deserializeAppointments(storedNotifications),
        [storedNotifications]
    );

    const setPendingAppointmentNotifications = useCallback(
        (value: SetStateAction<AppointmentNotification[]>) => {
            setStoredNotifications((prev) => {
                const current = deserializeAppointments(prev);
                const next = typeof value === "function" ? value(current) : value;
                return serializeAppointments(next);
            });
        },
        [setStoredNotifications]
    );

    const pendingCallsCount = pendingAppointmentNotifications.length;

    const dismissAppointmentNotification = useCallback(
        (id: string): void => {
            setPendingAppointmentNotifications((prev) => prev.filter((n) => n.id !== id));
        },
        [setPendingAppointmentNotifications]
    );

    const showNoLeadIdError = (): void => {
        toast.error(t("no_lead_id_available"));
    };

    const showAppointmentNotificationToast = (
        notification: AppointmentStatusUpdateNotification,
        onDetailsClick: (leadId: string) => void
    ): void => {
        const handleDetailsClick = () => {
            if (notification.leadId) {
                onDetailsClick(notification.leadId);
            } else {
                showNoLeadIdError();
            }
        };

        showNotificationToast({
            variant: "appointment",
            title: t("pending_appointments"),
            meta: [
                {
                    label: t("appointment_label"),
                    value: notification.appointmentId || t("not_available"),
                },
                {
                    label: t("brand_label"),
                    value: notification.brandName || t("not_available"),
                },
            ],
            action: {
                label: t("details"),
                onClick: handleDetailsClick,
            },
            duration: 5000,
        });
    };

    const handleAppointmentStatusUpdate = (
        data: AppointmentStatusUpdatePayload,
        onDetailsClick: (leadId: string) => void
    ): void => {
        const notification: AppointmentStatusUpdateNotification = {
            id: data.appointmentId,
            appointmentId: data.appointmentId,
            brandName: data.brandName,
            leadId: data.leadId,
            type: data.type,
            receivedAt: DateTime.now(),
        };

        setStoredNotifications((prev) => {
            const withoutDuplicate = prev.filter((n) => n.appointmentId !== notification.appointmentId);
            return [toStoredAppointment(notification), ...withoutDuplicate];
        });
        showAppointmentNotificationToast(notification, onDetailsClick);
    };

    const handleAppointmentRemoval = (appointmentId: string): void => {
        setPendingAppointmentNotifications((prev) => prev.filter((n) => n.appointmentId !== appointmentId));
    };

    return {
        pendingAppointmentNotifications,
        setPendingAppointmentNotifications,
        pendingCallsCount,
        dismissAppointmentNotification,
        handleAppointmentStatusUpdate,
        handleAppointmentRemoval,
        showAppointmentNotificationToast,
        showNoLeadIdError,
    };
};
